/**
 * Board meeting record — detail view.
 *
 * Ported from `dashboard/src/views/shareholder/Meetings.jsx`
 * (MeetingDetail subcomponent). Reads from
 * `GET /board/meetings/:id`. Non-board shareholders only see
 * concluded meetings; executive-session content is server-side
 * redacted from the payload.
 */
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Video } from 'lucide-react';
import { useBoardMeeting } from '@/hooks/useMeetings';
import { fmtDateTime } from '@/lib/format';
import {
  Card,
  SectionLabel,
  Skeleton,
  StatCard
} from '@/components/ui/atoms';
import type { MeetingItemResult } from '@/api/meetings';

const outcomeColor = (outcome: string) => {
  switch (outcome) {
    case 'passed':
      return {
        text: 'text-emerald-300',
        bg: 'bg-emerald-500/15',
        ring: 'ring-emerald-500/30'
      };
    case 'failed':
      return {
        text: 'text-red-300',
        bg: 'bg-red-500/15',
        ring: 'ring-red-500/30'
      };
    case 'no_quorum':
      return {
        text: 'text-amber-300',
        bg: 'bg-amber-500/15',
        ring: 'ring-amber-500/30'
      };
    default:
      return {
        text: 'text-nurev-muted',
        bg: 'bg-white/5',
        ring: 'ring-white/10'
      };
  }
};

const outcomeLabel = (outcome: string) => {
  if (outcome === 'no_quorum') return 'NO QUORUM';
  return outcome ? outcome.toUpperCase() : '';
};

function ResultRow({ result }: { result: MeetingItemResult }) {
  const c = outcomeColor(result.outcome);
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-nurev-border bg-white/[0.02] px-3 py-2.5">
      <div className="flex-1">
        <div className="text-sm font-semibold text-nurev-text">
          {result.title}
        </div>
        {result.itemType === 'binary' && (
          <div className="mt-0.5 text-[11px] text-nurev-muted">
            {result.sharesFor || 0} shares for ·{' '}
            {result.sharesAgainst || 0} against ·{' '}
            {result.votesAbstain || 0} abstained
          </div>
        )}
        {result.itemType === 'multi' && result.winner && (
          <div className="mt-0.5 text-[11px] text-nurev-muted">
            Winner: {result.winner}
          </div>
        )}
      </div>
      <span
        className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ${c.text} ${c.bg} ${c.ring}`}
      >
        {outcomeLabel(result.outcome)}
      </span>
    </div>
  );
}

export function MeetingDetailScreen() {
  const { meetingId } = useParams<{ meetingId: string }>();
  const { data, isLoading } = useBoardMeeting(meetingId);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const meeting = data?.meeting;
  const items = data?.items || [];
  if (!meeting) {
    return (
      <div className="space-y-4">
        <Link
          to="/meetings"
          className="inline-flex items-center gap-1 text-xs text-nurev-muted hover:text-nurev-text"
        >
          <ArrowLeft className="h-3 w-3" /> All meeting records
        </Link>
        <div className="rounded-xl border border-nurev-border bg-nurev-card p-6 text-sm text-nurev-muted">
          Meeting not found, or you don&apos;t have permission to view this
          record.
        </div>
      </div>
    );
  }

  const summary = meeting.resultsSummary;

  return (
    <div className="space-y-6">
      <Link
        to="/meetings"
        className="inline-flex items-center gap-1 text-xs text-nurev-muted hover:text-nurev-text"
      >
        <ArrowLeft className="h-3 w-3" /> All meeting records
      </Link>

      {/* Header */}
      <Card>
        <SectionLabel>
          {meeting.meetingType} · Concluded
        </SectionLabel>
        <h1 className="mt-1 text-2xl font-bold text-nurev-text">
          {meeting.name}
        </h1>
        <div className="mt-1 text-sm text-nurev-muted">
          {fmtDateTime(meeting.concludedAt || meeting.scheduledAt)} PT
        </div>
      </Card>

      {/* Summary stats */}
      {summary && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatCard
            label="Quorum"
            value={summary.quorumReached ? 'Met' : 'Not met'}
            color={
              summary.quorumReached ? 'text-emerald-300' : 'text-red-300'
            }
          />
          <StatCard
            label="Present"
            value={summary.attendance?.present ?? 0}
            color="text-purple-300"
          />
          <StatCard
            label="Items"
            value={items.length || summary.itemResults?.length || 0}
            color="text-amber-300"
          />
        </div>
      )}

      {/* Voting results */}
      {summary?.itemResults && summary.itemResults.length > 0 && (
        <Card>
          <div className="mb-3 text-sm font-semibold text-nurev-text">
            Voting outcomes
          </div>
          <div className="space-y-2">
            {summary.itemResults.map((r, idx) => (
              <ResultRow key={r.itemId || idx} result={r} />
            ))}
          </div>
        </Card>
      )}

      {/* Recording */}
      {meeting.recordingUrl && (
        <Card>
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-nurev-text">
            <Video className="h-4 w-4" /> Meeting recording
          </div>
          <a
            href={meeting.recordingUrl}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-emerald-300 underline-offset-2 hover:text-emerald-200 hover:underline"
          >
            {meeting.recordingUrl}
          </a>
        </Card>
      )}

      {/* Minutes */}
      {meeting.minutes && (
        <Card>
          <div className="mb-2 text-sm font-semibold text-nurev-text">
            Minutes
          </div>
          <div className="whitespace-pre-wrap text-sm text-nurev-text">
            {meeting.minutes}
          </div>
        </Card>
      )}
    </div>
  );
}
